/**
 * User Preferences Hook (PostgreSQL)
 *
 * Replaces Convex userPreferences queries/mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@mavenmm/teamwork-auth';

const API_BASE = '/api/user-preferences';

// Writes are rejected without a bearer token (see functions/user-preferences.ts).
function writeHeaders(token: string | null | undefined): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface UserPreferencesResponse {
  userId: string;
  preferences: Record<string, unknown>;
}

/**
 * Fetch user preferences
 */
export function useUserPreferences(userId: string) {
  return useQuery({
    queryKey: ['userPreferences', userId],
    queryFn: async (): Promise<Record<string, unknown>> => {
      const res = await fetch(`${API_BASE}?userId=${encodeURIComponent(userId)}`);
      if (!res.ok) {
        throw new Error(`Failed to fetch preferences: ${res.status}`);
      }
      const data: UserPreferencesResponse = await res.json();
      return data.preferences;
    },
    enabled: !!userId,
    // Board content uses a shorter stale time so urgent edits are visible sooner
    ...(userId === 'global-board-content' && { staleTime: 5 * 60 * 1000 }),
  });
}

/**
 * Update user preferences (full replace)
 */
export function useSetUserPreferences() {
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuthContext();

  return useMutation({
    mutationFn: async ({
      userId,
      preferences,
    }: {
      userId: string;
      preferences: Record<string, unknown>;
    }): Promise<UserPreferencesResponse> => {
      const res = await fetch(API_BASE, {
        method: 'PUT',
        credentials: 'include',
        headers: writeHeaders(getAccessToken()),
        body: JSON.stringify({ userId, preferences }),
      });
      if (!res.ok) {
        throw new Error(`Failed to save preferences: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data) => {
      // Invalidate and update cache
      queryClient.invalidateQueries({ queryKey: ['userPreferences', data.userId] });
    },
  });
}

/**
 * Atomic per-board content merge.
 *
 * Sends just `{ boardId, content }` to the server, which uses jsonb_set to
 * merge into preferences.boardContent[boardId] in a single SQL statement —
 * concurrent saves to different boards no longer clobber each other.
 */
export function useMergeBoardContent() {
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuthContext();

  return useMutation({
    mutationFn: async ({
      boardId,
      content,
    }: {
      boardId: string;
      content: string;
    }): Promise<UserPreferencesResponse> => {
      const res = await fetch(API_BASE, {
        method: 'PATCH',
        credentials: 'include',
        headers: writeHeaders(getAccessToken()),
        body: JSON.stringify({ userId: 'global-board-content', boardId, content }),
      });
      if (!res.ok) {
        throw new Error(`Failed to save board content: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data) => {
      // Replace the cached blob with the freshly-merged server state so the
      // local view reflects what other users have saved too, not a stale spread.
      queryClient.setQueryData(['userPreferences', data.userId], data.preferences);
    },
  });
}
